import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import type { UpstreamApiDashboardData } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { dataErrorHandling } from 'src/components/dashboard/helper';

const fetchDashboardData = async (
  endpoint: string,
  flatFilters: Record<string, string[]>,
  signal?: AbortSignal,
): Promise<UpstreamApiDashboardData> =>
  HttpService.post(
    endpoint,
    {
      ...flatFilters,
    },
    undefined,
    undefined,
    { signal },
  )
    .then((data) => Promise.resolve(data.data))
    .catch(dataErrorHandling);

const useStatisticsDataQuery = (
  enable: boolean,
  dashboardId?: number,
  filters: Record<string, string[]> = {},
) => {
  const { i18n } = useTranslation();

  const endpoint = useMemo(
    () =>
      generatePath(apiPaths.DASHBOARD.STATISTICS, { id: dashboardId ?? '' }),
    [dashboardId],
  );

  const flatFiltersLength = Object.keys(filters).length;
  return useQuery<UpstreamApiDashboardData, string>({
    queryKey: [endpoint, i18n.language, filters],
    queryFn: ({ signal }) => fetchDashboardData(endpoint, filters, signal),
    enabled: enable && !!dashboardId && flatFiltersLength > 0,
  });
};

export default useStatisticsDataQuery;
